import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from "react";
import { Alert, Box, Button, Card } from "@curves/ui-base";
import { Grid } from "@mui/material";
import { isEqual, refIsRequiredError } from "@util/utils";
import {
	InvoiceByCarrierNo,
	InvoiceByCarrierType,
	InvoiceByDonate,
	InvoiceByEmail,
	InvoiceByIssuingType,
	InvoiceByPhone,
	InvoiceByTriplicateUniform,
	useInvoiceFieldVisibility,
} from "@icoach/components/invoice";
import { INVOICE_CARRIER_TYPE, ORDER_INVOICE_ISSUING_TYPE } from "@icoach/constants/";
import { useMemberDefaultInvoice } from "@icoach/healthyCyclePurchase/hooks";
import { PickItem } from "@icoach/healthyCyclePurchase/components/index";

const InvoiceSection = forwardRef((props, ref) => {
	const { memberID, initialSource = {}, resource, errorModule, isSelected, onEffectTypeChange, pickItemRef } = props;

	// 使用 Hook 管理發票常用資料
	const { formData, applyDefaultInvoice } = useMemberDefaultInvoice(memberID, initialSource);

	// 從 formData.source 取得初始值
	const { issuingType: issuingTypeProps, invoiceCarrierType: carrierTypeProps, donationCode, issuingEmail, issuingPhone, carrierNo, taxID, taxTitle } = formData.source || {};

	// 管理發票類型狀態
	const [issuingType, setIssuingType] = useState(issuingTypeProps || ORDER_INVOICE_ISSUING_TYPE.DUPLICATEUNIFORMINVOICE);
	const [invoiceCarrierType, setInvoiceCarrierType] = useState(carrierTypeProps || INVOICE_CARRIER_TYPE.MEMBERCARRIER);

	// 使用 useInvoiceFieldVisibility 控制欄位顯示
	const fieldVisibility = useInvoiceFieldVisibility({
		issuingType,
		invoiceCarrierType,
		readOnly: false,
	});

	// Refs
	const issuingTypeRef = useRef(null);
	const donateRef = useRef(null);
	const invoiceCarrierTypeRef = useRef(null);
	const invoiceCarrierNoRef = useRef(null);
	const emailRef = useRef(null);
	const phoneRef = useRef(null);
	const triplicateUniformRef = useRef(null);

	const isNotificationSMS =
		isEqual(invoiceCarrierType, INVOICE_CARRIER_TYPE.SMSSENT);

	// 處理發票類型變更
	const handleChangeIssuingType = value => {
		setIssuingType(value);
	};

	// 處理載具類型變更
	const handleChangeInvoiceCarrierType = value => {
		setInvoiceCarrierType(value);
	};

	// 當 formData.source 變更時，同步更新狀態
	useEffect(() => {
		setIssuingType(issuingTypeProps);
		setInvoiceCarrierType(carrierTypeProps);
		// eslint-disable-next-line
	}, [formData.key]);

	// 取得表單結果
	const getResult = useCallback(() => {
		let result = {};

		if (issuingTypeRef.current && issuingTypeRef.current.getResult) {
			Object.assign(result, issuingTypeRef.current.getResult());
		}

		if (donateRef.current && donateRef.current.getResult) {
			Object.assign(result, donateRef.current.getResult());
		}

		if (invoiceCarrierTypeRef.current && invoiceCarrierTypeRef.current.getResult) {
			Object.assign(result, invoiceCarrierTypeRef.current.getResult());
		}

		if (invoiceCarrierNoRef.current && invoiceCarrierNoRef.current.getResult) {
			Object.assign(result, invoiceCarrierNoRef.current.getResult());
		}

		if (triplicateUniformRef.current && triplicateUniformRef.current.getResult) {
			Object.assign(result, triplicateUniformRef.current.getResult());
		}
		
		if (emailRef.current && emailRef.current.getResult) {
			Object.assign(result, emailRef.current.getResult());
		}

		if (phoneRef.current && phoneRef.current.getResult) {
			Object.assign(result, phoneRef.current.getResult());
		}

		// 轉換 issuingEmail 為 mail、issuingPhone 為 cellPhone，並包裝在 invoice 物件中
		// 避免循環引用：創建新物件而不是直接引用 result
		const { issuingEmail, issuingPhone, ...rest } = result;

		return {
			invoice: {
				...rest,
				mail: issuingEmail || "",
				cellPhone: issuingPhone || "",
			},
		};
	}, []);

	useImperativeHandle(
		ref,
		() => ({
			getResult,
			//前端雖顯示必填，但是不檢查信箱與手機號碼必填的錯誤
			isError: () => refIsRequiredError(issuingTypeRef, donateRef, invoiceCarrierNoRef, invoiceCarrierTypeRef, triplicateUniformRef),
		}),
		[getResult],
	);

	// 判斷是否為變更頁面模式（有選擇功能）
	const isChangeMode = onEffectTypeChange !== undefined;
	// 如果沒有傳入 isSelected，預設為 true（建立頁面模式）
	const shouldShow = isSelected !== undefined ? isSelected : true;

	const { invoice:invoiceError = {}  } = errorModule || {};

	return (
		<>
			<Card.CardHeader
				title={isChangeMode ? <PickItem title="發票資訊 (即時生效)" ref={pickItemRef} checked={isSelected} handleChange={onEffectTypeChange} /> : "發票資訊 (即時生效)"}
				className="bg-light-active px-4"
			/>
			{shouldShow && (
				<Card.CardContent key={formData.key} className="p-4">
					<Grid spacing={2} container>
						<Grid xs={12} item>
							<Box className="tool-btn-group">
								<Button color="gray" onClick={applyDefaultInvoice}>
									同會籍資料
								</Button>
							</Box>
							<br />
							<Alert severity="info" variant="filled" color="info" className="alert-justify-content-start">
								發票資訊變更將立即生效，若有其他選項變更將會以變更生效日為主
							</Alert>
						</Grid>
						<Grid xs={8} item>
							<Grid spacing={3} container>
								<Grid xs={12} item>
									<InvoiceByIssuingType
										ref={issuingTypeRef}
										issuingType={issuingType}
										options={resource?.invoiceIssuingOptions ?? []}
										errorData={invoiceError}
										onChange={handleChangeIssuingType}
									/>
								</Grid>

								{/* 捐贈發票 */}
								{fieldVisibility.showDonateField && (
									<Grid xs={12} item>
										<InvoiceByDonate ref={donateRef} donationCode={donationCode} errorData={invoiceError} />
									</Grid>
								)}

								{/* 二聯式發票 */}
								{fieldVisibility.isDuplicateInvoice && (
									<>
										{fieldVisibility.showCarrierTypeField && (
											<Grid xs={12} item>
												<InvoiceByCarrierType
													ref={invoiceCarrierTypeRef}
													carrierType={invoiceCarrierType}
													options={resource?.invoiceCarrierOptions ?? []}
													errorData={errorModule}
													onChange={handleChangeInvoiceCarrierType}
												/>
											</Grid>
										)}
										{fieldVisibility.showCarrierNoField && (
											<Grid xs={12} item>
												<InvoiceByCarrierNo ref={invoiceCarrierNoRef} carrierNo={carrierNo} carrierType={invoiceCarrierType} errorData={invoiceError} />
											</Grid>
										)}
									</>
								)}

								{/* 三聯式發票 */}
								{fieldVisibility.showTriplicateUniformField && (
									<InvoiceByTriplicateUniform ref={triplicateUniformRef} taxID={taxID} taxTitle={taxTitle} errorData={invoiceError} />
								)}

								{/* 發票 Email 和 Phone */}
								<Grid item xs={6}>
									<InvoiceByEmail ref={emailRef} issuingEmail={issuingEmail} errorData={{issuingEmail: invoiceError?.mail}} required={!isNotificationSMS} />
								</Grid>
								<Grid item xs={6}>
									<InvoiceByPhone label="通知手機號碼" ref={phoneRef} issuingPhone={issuingPhone} errorData={{issuingPhone: invoiceError?.cellPhone}} disabled />
								</Grid>
								<Grid xs={12} item>
									<Alert severity="info" variant="filled" color="info" className="alert-justify-content-start">
										若要修改手機號碼，請至會員總覽基本資料處理。
									</Alert>
								</Grid>
							</Grid>
						</Grid>
					</Grid>
				</Card.CardContent>
			)}
		</>
	);
});

InvoiceSection.displayName = "InvoiceSection";

export default InvoiceSection;
